
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Contract,
  JsonRpcProvider,
  formatUnits,
} from "ethers";


/* =========================================================
   CONFIG
========================================================= */

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

const CONTRACT_ADDRESS =
  process.env.REACT_APP_KIND_RUSH_CONTRACT_ADDRESS ||
  "0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";


/* =========================================================
   ABI
========================================================= */

const ABI = [
  "function getActiveCampaignId() view returns (uint256)",

  `function getLeaderboard(
      uint256 campaignId,
      uint256 offset,
      uint256 limit
    )
    view
    returns (
      (
        address wallet,
        uint8 status,
        uint8 reason,
        uint256 qualifyingUsd,
        uint256 tradeVolumeUsd,
        uint256 rewardUsd,
        uint256 claimsCompleted,
        uint256 tradeCount
      )[] rows,
      uint256 total
    )`,

  `function getUserTrades(
      uint256 campaignId,
      address wallet,
      uint256 offset,
      uint256 limit
    )
    view
    returns (
      (
        bytes32 tradeId,
        address wallet,
        address pair,
        uint256 kindAmount,
        uint256 usdValue,
        uint256 fullUsdValue,
        uint256 timestamp,
        bool verified
      )[] records,
      uint256 total
    )`,
];


/* =========================================================
   ENUM LABELS
========================================================= */

const STATUS = {
  0: "Not Registered",
  1: "Qualifying",
  2: "Active",
  3: "Completed",
  4: "Disqualified",
};

const REASON = {
  0: "None",
  1: "KIND Sold",
  2: "KIND Sent",
  3: "KIND Received",
  4: "Invalid Trade",
  5: "Unauthorized Contract",
  6: "Admin Emergency",
};


/* =========================================================
   HELPERS
========================================================= */

const ZERO = 0n;


const usd = (value) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  }
  catch {
    return "0.00";
  }
};


const token = (value) => {
  try {
    return Number(
      formatUnits(
        value ?? ZERO,
        18
      )
    ).toLocaleString(
      undefined,
      {
        maximumFractionDigits: 2,
      }
    );
  }
  catch {
    return "0";
  }
};


const shortAddress = (address) => {
  if (!address) return "—";

  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};


const dateText = (timestamp) => {
  const value =
    Number(
      timestamp || 0
    );

  if (!value) {
    return "—";
  }

  return new Date(
    value * 1000
  ).toLocaleString();
};


/* =========================================================
   FRONTEND REWARD RATE CALCULATION

   IMPORTANT:

   This does NOT read reward percentage
   from the backend.

   It calculates directly from qualifyingUsd.

   $0.01 - $50.00      = 100%
   $50.01 - $100.00    = 150%
   $100.01 - $150.00   = 200%
   $150.01 - $200.00   = 300%
========================================================= */

const getRewardRate = (qualifyingUsd) => {
  try {
    const value =
      Number(
        formatUnits(
          qualifyingUsd ?? ZERO,
          18
        )
      );

    if (
      !Number.isFinite(value) ||
      value <= 0
    ) {
      return 0;
    }

    if (
      value <= 50
    ) {
      return 100;
    }

    if (
      value <= 100
    ) {
      return 150;
    }

    if (
      value <= 150
    ) {
      return 200;
    }

    return 300;
  }
  catch {
    return 0;
  }
};


const getRewardRateText = (qualifyingUsd) => {
  const rate =
    getRewardRate(
      qualifyingUsd
    );

  return `${rate}%`;
};


/* =========================================================
   LEADERBOARD
========================================================= */

export default function Leaderboard() {
  const provider =
    useMemo(
      () =>
        new JsonRpcProvider(
          BSC_RPC
        ),
      []
    );


  const contract =
    useMemo(
      () => {
        if (
          !CONTRACT_ADDRESS ||
          CONTRACT_ADDRESS.includes(
            "PASTE_NEW"
          )
        ) {
          return null;
        }

        return new Contract(
          CONTRACT_ADDRESS,
          ABI,
          provider
        );
      },
      [
        provider,
      ]
    );


  /* =======================================================
     STATE
  ======================================================= */

  const [
    campaignId,
    setCampaignId,
  ] = useState(ZERO);

  const [
    leaderboard,
    setLeaderboard,
  ] = useState([]);

  const [
    totalUsers,
    setTotalUsers,
  ] = useState(0);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    selectedWallet,
    setSelectedWallet,
  ] = useState("");

  const [
    selectedTrades,
    setSelectedTrades,
  ] = useState([]);

  const [
    tradesOpen,
    setTradesOpen,
  ] = useState(false);

  const [
    tradesLoading,
    setTradesLoading,
  ] = useState(false);


  /* =======================================================
     LOAD LEADERBOARD
  ======================================================= */

  const loadLeaderboard =
    useCallback(
      async (
        showLoader = false
      ) => {
        if (!contract) {
          setLoading(false);

          return;
        }


        try {
          if (showLoader) {
            setLoading(true);
          }

          setError("");


          const activeId =
            await contract.getActiveCampaignId();


          setCampaignId(
            activeId
          );


          if (
            activeId === ZERO
          ) {
            setLeaderboard(
              []
            );

            setTotalUsers(
              0
            );

            return;
          }


          const rows = [];

          let offset = 0;

          const limit = 100;

          let total = 1;


          while (
            offset < total
          ) {
            const result =
              await contract.getLeaderboard(
                activeId,
                offset,
                limit
              );


            const page =
              result[0] || [];

            total =
              Number(
                result[1] || 0
              );


            page.forEach(
              (row) => {
                rows.push({
                  wallet:
                    row.wallet,

                  status:
                    Number(
                      row.status
                    ),

                  reason:
                    Number(
                      row.reason
                    ),

                  qualifyingUsd:
                    row.qualifyingUsd,

                  tradeVolumeUsd:
                    row.tradeVolumeUsd,

                  rewardUsd:
                    row.rewardUsd,

                  claimsCompleted:
                    Number(
                      row.claimsCompleted
                    ),

                  tradeCount:
                    Number(
                      row.tradeCount
                    ),
                });
              }
            );


            if (
              page.length === 0
            ) {
              break;
            }


            offset +=
              page.length;
          }


          /*
           * Highest full trade volume first.
           */
          rows.sort(
            (a, b) => {
              if (
                a.tradeVolumeUsd ===
                b.tradeVolumeUsd
              ) {
                return 0;
              }

              return (
                a.tradeVolumeUsd >
                b.tradeVolumeUsd
                  ? -1
                  : 1
              );
            }
          );


          setLeaderboard(
            rows
          );

          setTotalUsers(
            total
          );
        }
        catch (err) {
          console.error(
            err
          );

          setError(
            err?.shortMessage ||
            err?.message ||
            "Failed to load leaderboard."
          );
        }
        finally {
          setLoading(false);
        }
      },
      [
        contract,
      ]
    );


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {
      loadLeaderboard(
        true
      );


      const timer =
        setInterval(
          () => {
            loadLeaderboard(
              false
            );
          },
          12000
        );


      return () =>
        clearInterval(
          timer
        );
    },
    [
      loadLeaderboard,
    ]
  );


  const getCalculatedReward = (qualifyingUsd) => {
  try {
    const qualifying =
      Number(
        formatUnits(
          qualifyingUsd ?? ZERO,
          18
        )
      );

    if (
      !Number.isFinite(qualifying) ||
      qualifying <= 0
    ) {
      return "0.00";
    }

    let rate = 0;

    if (qualifying <= 50) {
      rate = 100;
    }
    else if (qualifying <= 100) {
      rate = 150;
    }
    else if (qualifying <= 150) {
      rate = 200;
    }
    else {
      rate = 300;
    }

    const reward =
      qualifying *
      (rate / 100);

    return reward.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  }
  catch {
    return "0.00";
  }
};

  /* =======================================================
     LOAD USER TRADES
  ======================================================= */

  const openTrades =
    useCallback(
      async (
        wallet
      ) => {
        if (
          !contract ||
          campaignId === ZERO
        ) {
          return;
        }


        try {
          setSelectedWallet(
            wallet
          );

          setSelectedTrades(
            []
          );

          setTradesOpen(
            true
          );

          setTradesLoading(
            true
          );


          const records = [];

          let offset = 0;

          const limit = 100;

          let total = 1;


          while (
            offset < total
          ) {
            const result =
              await contract.getUserTrades(
                campaignId,
                wallet,
                offset,
                limit
              );


            const page =
              result[0] || [];

            total =
              Number(
                result[1] || 0
              );


            page.forEach(
              (trade) => {
                records.push({
                  tradeId:
                    trade.tradeId,

                  wallet:
                    trade.wallet,

                  pair:
                    trade.pair,

                  kindAmount:
                    trade.kindAmount,

                  qualifyingUsd:
                    trade.usdValue,

                  fullUsdValue:
                    trade.fullUsdValue,

                  timestamp:
                    trade.timestamp,

                  verified:
                    trade.verified,
                });
              }
            );


            if (
              page.length === 0
            ) {
              break;
            }


            offset +=
              page.length;
          }


          records.sort(
            (a, b) =>
              Number(
                b.timestamp
              ) -
              Number(
                a.timestamp
              )
          );


          setSelectedTrades(
            records
          );
        }
        catch (err) {
          console.error(
            err
          );

          setError(
            err?.shortMessage ||
            err?.message ||
            "Failed to load trades."
          );
        }
        finally {
          setTradesLoading(
            false
          );
        }
      },
      [
        contract,
        campaignId,
      ]
    );


  /* =======================================================
     CONTRACT ADDRESS ERROR
  ======================================================= */

  if (
    !CONTRACT_ADDRESS ||
    CONTRACT_ADDRESS.includes(
      "PASTE_NEW"
    )
  ) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          Add your deployed contract address.
        </div>
      </div>
    );
  }


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.empty}>
          Loading leaderboard...
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}

        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>
              KIND CAT TOKEN
            </div>

            <h1 style={styles.title}>
              Wallet Rush Leaderboard
            </h1>

            <div style={styles.subtitle}>
              {campaignId === ZERO
                ? "No active campaign"
                : `Campaign #${campaignId.toString()}`}
            </div>
          </div>


          <div style={styles.headerRight}>
            <div style={styles.usersBadge}>
              {totalUsers.toLocaleString()} Users
            </div>

            <button
              style={styles.refreshButton}
              onClick={() =>
                loadLeaderboard(
                  true
                )
              }
            >
              Refresh
            </button>
          </div>
        </div>


        {/* TOP 3 */}

        {leaderboard.length > 0 && (
          <div style={styles.topGrid}>
            {leaderboard
              .slice(
                0,
                3
              )
              .map(
                (
                  row,
                  index
                ) => (
                  <div
                    key={
                      row.wallet
                    }
                    style={{
                      ...styles.topCard,

                      ...(index ===
                      0
                        ? styles.firstCard
                        : {}),
                    }}
                  >
                    <div style={styles.rankCircle}>
                      #{index + 1}
                    </div>

                    <div style={styles.topWallet}>
                      {shortAddress(
                        row.wallet
                      )}
                    </div>

                    <div style={styles.topVolume}>
                      $
                      {usd(
                        row.tradeVolumeUsd
                      )}
                    </div>

                    <div style={styles.topLabel}>
                      TOTAL TRADE VOLUME
                    </div>

                    <div style={styles.topStats}>
                      <div>
                        <span style={styles.miniLabel}>
                          QUALIFYING
                        </span>

                        <strong>
                          $
                          {usd(
                            row.qualifyingUsd
                          )}
                        </strong>
                      </div>


                      <div>
                        <span style={styles.miniLabel}>
                          REWARD RATE
                        </span>

                        <strong style={styles.rewardRate}>
                          {getRewardRateText(
                            row.qualifyingUsd
                          )}
                        </strong>
                      </div>


                      <div>
                        <span style={styles.miniLabel}>
                          TRADES
                        </span>

                        <strong>
                          {row.tradeCount}
                        </strong>
                      </div>


                      <div>
                        <span style={styles.miniLabel}>
                          CLAIMS
                        </span>

                        <strong>
                          {row.claimsCompleted}/4
                        </strong>
                      </div>
                    </div>
                  </div>
                )
              )}
          </div>
        )}


        {/* TABLE */}

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelEyebrow}>
                COMMUNITY
              </div>

              <h2 style={styles.panelTitle}>
                All Participants
              </h2>
            </div>

            <div style={styles.sortLabel}>
              Sorted by Trade Volume
            </div>
          </div>


          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    Rank
                  </th>

                  <th style={styles.th}>
                    Wallet
                  </th>

                  <th style={styles.th}>
                    Status
                  </th>

                  <th style={styles.th}>
                    Qualifying
                  </th>

                  <th style={styles.th}>
                    Reward Rate
                  </th>

                  <th style={styles.th}>
                    Trade Volume
                  </th>

                  <th style={styles.th}>
                    Reward
                  </th>

                  <th style={styles.th}>
                    Claims
                  </th>

                  <th style={styles.th}>
                    Trades
                  </th>

                  <th style={styles.th}>
                    History
                  </th>
                </tr>
              </thead>


              <tbody>
                {leaderboard.map(
                  (
                    row,
                    index
                  ) => (
                    <tr
                      key={
                        row.wallet
                      }
                      style={styles.tr}
                    >
                      <td style={styles.td}>
                        <div
                          style={{
                            ...styles.rank,

                            ...(index ===
                            0
                              ? styles.rankFirst
                              : {}),

                            ...(index ===
                            1
                              ? styles.rankSecond
                              : {}),

                            ...(index ===
                            2
                              ? styles.rankThird
                              : {}),
                          }}
                        >
                          {index + 1}
                        </div>
                      </td>


                      <td style={styles.td}>
                        <div style={styles.walletCell}>
                          <div style={styles.avatar}>
                            {row.wallet
                              .slice(
                                2,
                                4
                              )
                              .toUpperCase()}
                          </div>

                          <span style={styles.walletText}>
                            {shortAddress(
                              row.wallet
                            )}
                          </span>
                        </div>
                      </td>


                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.status,

                            ...(row.status ===
                            4
                              ? styles.statusBad
                              : {}),

                            ...(row.status ===
                            3
                              ? styles.statusDone
                              : {}),
                          }}
                        >
                          {
                            STATUS[
                              row.status
                            ]
                          }
                        </span>
                      </td>


                      <td style={styles.td}>
                        $
                        {usd(
                          row.qualifyingUsd
                        )}
                      </td>


                      <td style={styles.td}>
                        <span style={styles.rewardBadge}>
                          {getRewardRateText(
                            row.qualifyingUsd
                          )}
                        </span>
                      </td>


                      <td style={styles.td}>
                        <strong style={styles.volume}>
                          $
                          {usd(
                            row.tradeVolumeUsd
                          )}
                        </strong>
                      </td>


                   <td style={styles.td}>
  <strong style={styles.rewardAmount}>
    $
    {getCalculatedReward(
      row.qualifyingUsd
    )}
  </strong>
</td>


                      <td style={styles.td}>
                        {row.claimsCompleted}/4
                      </td>


                      <td style={styles.td}>
                        {row.tradeCount}
                      </td>


                      <td style={styles.td}>
                        <button
                          style={styles.viewButton}
                          onClick={() =>
                            openTrades(
                              row.wallet
                            )
                          }
                        >
                          View Trades
                        </button>
                      </td>
                    </tr>
                  )
                )}


                {leaderboard.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan="10"
                      style={styles.noRows}
                    >
                      No participants found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>


        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}
      </div>


      {/* TRADE HISTORY */}

      {tradesOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.panelEyebrow}>
                  VERIFIED TRADE HISTORY
                </div>

                <h2 style={styles.modalTitle}>
                  {shortAddress(
                    selectedWallet
                  )}
                </h2>
              </div>


              <button
                style={styles.closeButton}
                onClick={() =>
                  setTradesOpen(
                    false
                  )
                }
              >
                ×
              </button>
            </div>


            <div style={styles.modalBody}>
              {tradesLoading ? (
                <div style={styles.emptyTrades}>
                  Loading trades...
                </div>
              ) : selectedTrades.length ===
                0 ? (
                <div style={styles.emptyTrades}>
                  No verified trades found.
                </div>
              ) : (
                selectedTrades.map(
                  (
                    trade,
                    index
                  ) => (
                    <div
                      key={
                        trade.tradeId
                      }
                      style={styles.tradeCard}
                    >
                      <div style={styles.tradeHeader}>
                        <strong>
                          Trade #
                          {selectedTrades.length -
                            index}
                        </strong>

                        <span style={styles.verified}>
                          Verified
                        </span>
                      </div>


                      <div style={styles.tradeGrid}>
                        <TradeItem
                          label="Full Trade"
                          value={`$${usd(
                            trade.fullUsdValue
                          )}`}
                        />

                        <TradeItem
                          label="Qualifying"
                          value={`$${usd(
                            trade.qualifyingUsd
                          )}`}
                        />

                        <TradeItem
                          label="KIND Bought"
                          value={`${token(
                            trade.kindAmount
                          )} KIND`}
                        />

                        <TradeItem
                          label="Date"
                          value={dateText(
                            trade.timestamp
                          )}
                        />
                      </div>


                      <div style={styles.tradeId}>
                        {trade.tradeId}
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================
   TRADE ITEM
========================================================= */

function TradeItem({
  label,
  value,
}) {
  return (
    <div>
      <div style={styles.tradeLabel}>
        {label}
      </div>

      <div style={styles.tradeValue}>
        {value}
      </div>
    </div>
  );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100vh",
    padding: "32px 18px 80px",
    color: "#ffffff",
    background:
      "radial-gradient(circle at 20% 0%, rgba(255,204,102,.18), transparent 32%), #07090d",
    fontFamily: "Inter, Arial, sans-serif",
  },

  container: {
    maxWidth: "1450px",
    margin: "0 auto",
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "18px",
    marginBottom: "25px",
  },

  eyebrow: {
    color: "#ffcc66",
    fontSize: "11px",
    fontWeight: 900,
    letterSpacing: "2px",
  },

  title: {
    margin: "6px 0 0",
    fontSize: "34px",
    fontWeight: 900,
  },

  subtitle: {
    marginTop: "8px",
    color: "#757b8a",
    fontSize: "13px",
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },

  usersBadge: {
    padding: "11px 15px",
    borderRadius: "12px",
    background: "#11151c",
    border: "1px solid #242a35",
    color: "#a8adb9",
    fontSize: "12px",
    fontWeight: 700,
  },

  refreshButton: {
    padding: "11px 17px",
    border: 0,
    borderRadius: "12px",
    cursor: "pointer",
    color: "#fff",
    fontWeight: 800,
    background: "linear-gradient(135deg,#ffcc66,#e5ad45)",
  },

  topGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
    gap: "14px",
    marginBottom: "18px",
  },

  topCard: {
    padding: "22px",
    borderRadius: "20px",
    border: "1px solid #222936",
    background: "linear-gradient(145deg,#11151d,#0d1016)",
    minWidth: 0,
  },

  firstCard: {
    border: "1px solid rgba(255,204,102,.65)",
    background:
      "linear-gradient(145deg,rgba(255,204,102,.23),#10131a)",
  },

  rankCircle: {
    width: "38px",
    height: "38px",
    display: "grid",
    placeItems: "center",
    borderRadius: "12px",
    background: "#1a1e29",
    color: "#ffcc66",
    fontWeight: 900,
  },

  topWallet: {
    marginTop: "18px",
    color: "#c7cbd3",
    fontFamily: "monospace",
    fontSize: "14px",
    overflowWrap: "anywhere",
  },

  topVolume: {
    marginTop: "9px",
    fontSize: "29px",
    fontWeight: 900,
  },

  topLabel: {
    marginTop: "4px",
    color: "#686f7d",
    fontSize: "9px",
    letterSpacing: "1px",
    fontWeight: 800,
  },

  topStats: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 90px), 1fr))",
    gap: "10px",
    marginTop: "19px",
  },

  miniLabel: {
    display: "block",
    color: "#646b78",
    fontSize: "8px",
    marginBottom: "5px",
  },

  rewardRate: {
    color: "#ffcc66",
  },

  rewardBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "54px",
    padding: "6px 9px",
    borderRadius: "9px",
    color: "#ffcc66",
    background: "rgba(255,204,102,.10)",
    border: "1px solid rgba(255,204,102,.28)",
    fontSize: "10px",
    fontWeight: 900,
  },

  panel: {
    borderRadius: "22px",
    border: "1px solid #202631",
    padding: "21px",
    background: "linear-gradient(145deg,#10131a,#0b0e13)",
    minWidth: 0,
  },

  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "15px",
    marginBottom: "16px",
  },

  panelEyebrow: {
    color: "#ffcc66",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "1.5px",
  },

  panelTitle: {
    margin: "5px 0 0",
    fontSize: "21px",
  },

  sortLabel: {
    color: "#717887",
    fontSize: "11px",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  table: {
    width: "100%",
    minWidth: "1120px",
    borderCollapse: "separate",
    borderSpacing: "0 7px",
  },

  th: {
    textAlign: "left",
    color: "#646b79",
    fontSize: "9px",
    textTransform: "uppercase",
    letterSpacing: ".8px",
    padding: "10px 12px",
  },

  tr: {
    background: "#0a0d12",
  },

  td: {
    padding: "13px 12px",
    fontSize: "13px",
    borderTop: "1px solid #181d26",
    borderBottom: "1px solid #181d26",
  },

  rank: {
    width: "31px",
    height: "31px",
    display: "grid",
    placeItems: "center",
    borderRadius: "9px",
    background: "#171b24",
    fontWeight: 900,
  },

  rankFirst: {
    background: "rgba(255,204,102,.25)",
    color: "#ffcc66",
  },

  rankSecond: {
    color: "#d0d4dc",
  },

  rankThird: {
    color: "#c99b77",
  },

  walletCell: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },

  avatar: {
    width: "31px",
    height: "31px",
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    background: "#181c26",
    color: "#ffcc66",
    fontSize: "10px",
    fontWeight: 900,
    flexShrink: 0,
  },

  walletText: {
    fontFamily: "monospace",
    color: "#c6cad3",
  },

  status: {
    color: "#61e596",
    fontSize: "10px",
    fontWeight: 900,
  },

  statusBad: {
    color: "#ff6b81",
  },

  statusDone: {
    color: "#ffcc66",
  },

  volume: {
    color: "#fff",
  },

  viewButton: {
    border: "1px solid rgba(255,204,102,.35)",
    borderRadius: "9px",
    padding: "8px 11px",
    background: "rgba(255,204,102,.08)",
    color: "#ffcc66",
    cursor: "pointer",
    fontWeight: 800,
  },

  noRows: {
    padding: "40px",
    textAlign: "center",
    color: "#717887",
  },

  error: {
    marginTop: "15px",
    padding: "13px",
    borderRadius: "12px",
    background: "rgba(255,84,105,.08)",
    border: "1px solid rgba(255,84,105,.25)",
    color: "#ff8293",
  },

  empty: {
    margin: "120px auto",
    maxWidth: "500px",
    padding: "35px",
    borderRadius: "20px",
    textAlign: "center",
    color: "#858c99",
    background: "#101319",
    border: "1px solid #222732",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    display: "grid",
    placeItems: "center",
    padding: "20px",
    background: "rgba(0,0,0,.78)",
    backdropFilter: "blur(8px)",
    zIndex: 100,
  },

  modal: {
    width: "min(900px,100%)",
    maxHeight: "85vh",
    overflow: "hidden",
    borderRadius: "22px",
    background: "#0c0f14",
    border: "1px solid #2a303d",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "20px",
    borderBottom: "1px solid #202530",
  },

  modalTitle: {
    margin: "5px 0 0",
  },

  closeButton: {
    width: "38px",
    height: "38px",
    flexShrink: 0,
    borderRadius: "10px",
    border: "1px solid #292f3b",
    background: "#171a22",
    color: "#fff",
    cursor: "pointer",
    fontSize: "21px",
  },

  modalBody: {
    padding: "18px",
    maxHeight: "calc(85vh - 85px)",
    overflowY: "auto",
  },

  emptyTrades: {
    padding: "35px",
    textAlign: "center",
    color: "#747b89",
  },

  tradeCard: {
    padding: "16px",
    marginBottom: "10px",
    borderRadius: "15px",
    background: "#11151c",
    border: "1px solid #212732",
  },

  tradeHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "13px",
  },

  verified: {
    color: "#5ce192",
    fontSize: "10px",
    fontWeight: 900,
  },

  tradeGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
    gap: "12px",
  },

  tradeLabel: {
    color: "#666d7a",
    fontSize: "9px",
    textTransform: "uppercase",
  },

  tradeValue: {
    marginTop: "5px",
    fontSize: "13px",
    fontWeight: 700,
  },

  tradeId: {
    marginTop: "13px",
    color: "#545b68",
    fontSize: "9px",
    fontFamily: "monospace",
    overflowWrap: "anywhere",
  },
};

